import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, Plus, Trash2, Edit, Save, RotateCcw, Settings, Clock, Info, AlertCircle, 
  Target, Users, Bell, Mail, Phone, MessageSquare, Zap, BarChart2, TrendingUp, TrendingDown
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const PrioritiesManagement = () => {
  const [priorities, setPriorities] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPriority, setNewPriority] = useState({
    name: '',
    level: 1,
    color: '#dc2626',
    icon: 'AlertTriangle',
    description: '',
    slaTargets: { response: 15, resolution: 240 },
    escalationRules: [],
    autoAssignment: { enabled: true, team: '', skill: '' },
    notifications: { email: true, sms: false, slack: false, phone: false }
  });
  const [editingPriority, setEditingPriority] = useState(null);
  const [activeTab, setActiveTab] = useState('priorities'); // 'priorities', 'escalation', 'analytics'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [prioritiesResponse, escalationRulesResponse, teamsResponse] = await Promise.all([
        MockDataService.getPriorities(),
        MockDataService.getEscalationRules(),
        MockDataService.getTeams()
      ]);

      if (prioritiesResponse.success) {
        setPriorities(prioritiesResponse.data || []);
      } else {
        setPriorities([]);
        setError(prioritiesResponse.message || 'Failed to load priorities');
      }
      
      if (escalationRulesResponse.success) {
        setEscalationRules(escalationRulesResponse.data || []);
      } else {
        setEscalationRules([]);
      }
      
      if (teamsResponse.success) {
        setTeams(teamsResponse.data || []);
      } else {
        setTeams([]);
      }
    } catch (err) {
      setError('Failed to fetch priorities data.');
      setPriorities([]);
      setEscalationRules([]);
      setTeams([]);
      console.error('Error fetching priorities data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePriority = async () => {
    try {
      const response = await MockDataService.createPriority(newPriority);
      if (response.success) {
        setPriorities([...priorities, response.data]);
        setShowCreateForm(false);
        setNewPriority({
          name: '',
          level: 1,
          color: '#dc2626',
          icon: 'AlertTriangle',
          description: '',
          slaTargets: { response: 15, resolution: 240 },
          escalationRules: [],
          autoAssignment: { enabled: true, team: '', skill: '' },
          notifications: { email: true, sms: false, slack: false, phone: false }
        });
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to create priority.');
      console.error('Error creating priority:', err);
    }
  };

  const handleUpdatePriority = async (priorityId, updatedData) => {
    try {
      const response = await MockDataService.updatePriority(priorityId, updatedData);
      if (response.success) {
        setPriorities(priorities.map(p => p.id === priorityId ? response.data : p));
        setEditingPriority(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to update priority.');
      console.error('Error updating priority:', err);
    }
  };

  const handleDeletePriority = async (priorityId) => {
    try {
      const response = await MockDataService.deletePriority(priorityId);
      if (response.success) {
        setPriorities(priorities.filter(p => p.id !== priorityId));
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to delete priority.');
      console.error('Error deleting priority:', err);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      AlertTriangle,
      AlertCircle,
      Clock,
      Info
    };
    return icons[iconName] || AlertTriangle;
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
    return `${Math.floor(minutes / 1440)}d`;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
        <AlertTriangle className="mr-3" size={28} />
        Priorities Management
      </h1>
      <p className="text-gray-700 dark:text-gray-300 mb-8">
        Configure priority levels, escalation rules, and SLA targets to ensure tickets are handled appropriately based on their business impact.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'priorities', label: 'Priorities', icon: AlertTriangle },
              { id: 'escalation', label: 'Escalation Rules', icon: TrendingUp },
              { id: 'analytics', label: 'Analytics', icon: BarChart2 }
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-[#006600] text-[#006600]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <IconComponent className="mr-2" size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading priorities...</span>
        </div>
      )}

      {/* Priorities Tab */}
      {!loading && activeTab === 'priorities' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Priority Levels ({priorities?.length || 0})
            </h2>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
              style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}>
              <Plus className="mr-2" size={16} />
              Add Priority
            </button>
          </div>

          {/* Create Priority Form */}
          {showCreateForm && (
            <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Create New Priority
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority Name
                  </label>
                  <input
                    type="text"
                    value={newPriority.name}
                    onChange={(e) => setNewPriority({ ...newPriority, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="e.g., Critical, High, Medium, Low"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority Level
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={newPriority.level}
                    onChange={(e) => setNewPriority({ ...newPriority, level: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color
                  </label>
                  <input
                    type="color"
                    value={newPriority.color}
                    onChange={(e) => setNewPriority({ ...newPriority, color: e.target.value })}
                    className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Icon
                  </label>
                  <select
                    value={newPriority.icon}
                    onChange={(e) => setNewPriority({ ...newPriority, icon: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    <option value="AlertTriangle">Alert Triangle</option>
                    <option value="AlertCircle">Alert Circle</option>
                    <option value="Clock">Clock</option>
                    <option value="Info">Info</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newPriority.description}
                    onChange={(e) => setNewPriority({ ...newPriority, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    rows="3"
                    placeholder="Describe when this priority should be used"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Response Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={newPriority.slaTargets?.response || 0}
                    onChange={(e) => setNewPriority({ 
                      ...newPriority, 
                      slaTargets: { ...(newPriority.slaTargets || {}), response: parseInt(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Resolution Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={newPriority.slaTargets?.resolution || 0}
                    onChange={(e) => setNewPriority({ 
                      ...newPriority, 
                      slaTargets: { ...(newPriority.slaTargets || {}), resolution: parseInt(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePriority}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
                >
                  <Save className="mr-2" size={16} />
                  Create Priority
                </button>
              </div>
            </div>
          )}

          {/* Priorities List */}
          <div className="space-y-4">
            {priorities && priorities.length > 0 ? priorities.map((priority) => {
              const IconComponent = getIconComponent(priority.icon);
              return (
                <div key={priority.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                        style={{ backgroundColor: priority.color + '20' }}
                      >
                        <IconComponent 
                          size={24} 
                          style={{ color: priority.color }}
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {priority.name}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          Level {priority.level} • {priority.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingPriority(priority.id)}
                        className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeletePriority(priority.id)}
                        className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <Target className="mr-2" size={16} />
                        SLA Targets
                      </h4>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div>Response: {formatTime(priority.slaTargets?.response || 0)}</div>
                        <div>Resolution: {formatTime(priority.slaTargets?.resolution || 0)}</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <Users className="mr-2" size={16} />
                        Auto Assignment
                      </h4>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div>Team: {priority.autoAssignment?.team || 'Not assigned'}</div>
                        <div>Skill: {priority.autoAssignment?.skill || 'Not specified'}</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <Bell className="mr-2" size={16} />
                        Notifications
                      </h4>
                      <div className="flex space-x-2">
                        {priority.notifications?.email && <Mail size={16} className="text-blue-600" />}
                        {priority.notifications?.sms && <MessageSquare size={16} className="text-green-600" />}
                        {priority.notifications?.slack && <Zap size={16} className="text-purple-600" />}
                        {priority.notifications?.phone && <Phone size={16} className="text-orange-600" />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-12">
                <AlertTriangle size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Priorities Found</h3>
                <p className="text-gray-600 mb-4">Get started by creating your first priority level.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto"
                >
                  <Plus size={16} className="inline mr-2" />
                  Create Priority
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Escalation Rules Tab */}
      {activeTab === 'escalation' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Escalation Rules ({escalationRules?.length || 0})
            </h2>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
              <Plus className="mr-2" size={16} />
              Add Rule
            </button>
          </div>

          <div className="space-y-4">
            {escalationRules && escalationRules.length > 0 ? escalationRules.map((rule) => (
              <div key={rule.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {rule.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {rule.description}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400">
                      <Edit size={16} />
                    </button>
                    <button className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Condition</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{rule.condition}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Action</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{rule.action}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Threshold</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{formatTime(rule.timeThreshold)}</p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="text-center py-12">
                <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Escalation Rules Found</h3>
                <p className="text-gray-600 mb-4">Create escalation rules to automatically escalate tickets based on time thresholds.</p>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto">
                  <Plus size={16} className="inline mr-2" />
                  Add Escalation Rule
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Priority Analytics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                  <AlertTriangle className="text-red-600 dark:text-red-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Critical Tickets</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">12</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <AlertCircle className="text-orange-600 dark:text-orange-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">28</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                  <Clock className="text-yellow-600 dark:text-yellow-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Medium Priority</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">156</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Info className="text-green-600 dark:text-green-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Low Priority</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">89</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                SLA Compliance by Priority
              </h3>
              <div className="space-y-4">
                {priorities && priorities.length > 0 ? priorities.map((priority) => {
                  const IconComponent = getIconComponent(priority.icon);
                  const compliance = Math.random() * 20 + 80; // Mock compliance percentage
                  return (
                    <div key={priority.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <IconComponent 
                          size={20} 
                          style={{ color: priority.color }}
                          className="mr-3"
                        />
                        <span className="text-gray-900 dark:text-white">{priority.name}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-3">
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              width: `${compliance}%`,
                              backgroundColor: priority.color 
                            }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {compliance.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-8">
                    <BarChart2 size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No priority data available for analytics</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Escalation Trends
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">No Response Escalations</span>
                  <div className="flex items-center">
                    <TrendingUp className="text-red-500 mr-2" size={16} />
                    <span className="text-red-600 dark:text-red-400">+12%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Resolution Escalations</span>
                  <div className="flex items-center">
                    <TrendingDown className="text-green-500 mr-2" size={16} />
                    <span className="text-green-600 dark:text-green-400">-8%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Auto Assignment Success</span>
                  <div className="flex items-center">
                    <TrendingUp className="text-green-500 mr-2" size={16} />
                    <span className="text-green-600 dark:text-green-400">+5%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrioritiesManagement;